import React, { useState, useEffect, useRef, useCallback } from "react";
import { addDoc, serverTimestamp, collection } from "firebase/firestore";
import { db, storage } from "../authentication/firebase/firebase";
import { onSnapshot, query, where, orderBy } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "./forum-page.scss";
import ChatRoom from "../chat-room/chat-room";
import Logo from "../images/academates-logo.png";

const ForumPage = ({
  forumAddress,
  username,
  setSelectedForum,
  showMenu,
  setShowMenu,
}) => {
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostText, setNewPostText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const [loadPosts, setLoadPosts] = useState([]);
  const [displayChatroom, setDisplayChatroom] = useState(false);
  const [chatroomId, setChatroomId] = useState(null);
  const [createPostSelected, setCreatePostSelected] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [chatroomTitle, setChatroomTitle] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [displayForumNone, setDisplayForumNone] = useState(false);
  const [numOfPosts, setNumOfPosts] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [firstPostsHaveLoaded, setFirstPostsHaveLoaded] = useState(false);

  const screenWidth = window.innerWidth;
  const displayPostsRef = useRef();

  const postsCollectionRef = collection(db, "posts");

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);

    if (e.target.files[0]) {
      const imageURL = URL.createObjectURL(e.target.files[0]);
      setSelectedImagePreview(imageURL);
    }
  };

  const handleDeselectImage = () => {
    setSelectedFile(null);
    setSelectedImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    sendPost();
  };

  const sendPost = async () => {
    let imageURL = null;

    if (selectedFile) {
      const storageRef = ref(storage, `images/${selectedFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      await uploadTask.then(async (snapshot) => {
        const downloadURL = await getDownloadURL(snapshot.ref);
        imageURL = downloadURL;
      });
    }
    if (newPostTitle === "") {
      alert("Please add a title to your post.");
    } else {
      if (newPostTitle || newPostText || imageURL) {
        await addDoc(postsCollectionRef, {
          title: newPostTitle,
          text: newPostText,
          image: imageURL,
          createdAt: serverTimestamp(),
          username: username,
          chatroomId: crypto.randomUUID(),
          forumAddress,
        });

        setNewPostTitle("");
        setNewPostText("");
        setSelectedFile(null);
        setSelectedImagePreview(null);
      }
    }
    setCreatePostSelected(false);
  };

  const handleLoadMorePosts = useCallback(() => {
    const scrollContainer = displayPostsRef.current;
    const threshold = 100;

    if (
      !isLoading &&
      hasMore &&
      scrollContainer.scrollHeight -
        (scrollContainer.scrollTop + scrollContainer.clientHeight) <
        threshold
    ) {
      setIsLoading(true);
      setNumOfPosts((prev) => prev + 10);
    }
  }, [isLoading, hasMore]);

  useEffect(() => {
    const queryMessages = query(
      postsCollectionRef,
      where("forumAddress", "==", forumAddress),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(queryMessages, (snapshot) => {
      let postData = [];
      snapshot.forEach((doc) => {
        postData.push({ ...doc.data(), id: doc.id });
      });

      // Filter posts based on search criteria
      const filteredData = postData.filter(
        (post) =>
          post.title.toLowerCase().includes(searchInput.toLowerCase()) ||
          post.username.toLowerCase().includes(searchInput.toLowerCase()) ||
          post.text.toLowerCase().includes(searchInput.toLowerCase())
      );

      // Set hasMore based on whether there are more posts available
      setHasMore(filteredData.length > numOfPosts);

      // Apply the limit to the filtered posts
      const limitedData = filteredData.slice(0, numOfPosts);

      setLoadPosts(limitedData);
      setFirstPostsHaveLoaded(true);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [forumAddress, searchInput, numOfPosts, postsCollectionRef]);

  useEffect(() => {
    const currentRef = displayPostsRef.current;

    const handleScroll = () => {
      handleLoadMorePosts();
    };

    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading, hasMore, handleLoadMorePosts]);

  const enterChatroom = (post) => {
    setDisplayChatroom(true);
    setSelectedPost(post);
    setChatroomId(post.chatroomId);
    setChatroomTitle(post.title);
    // setShowMenu(false);
  };

  function formatTimestamp(timestamp) {
    const date = timestamp.toDate();

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    return date.toLocaleString("en-US", options);
  }

  useEffect(() => {
    if (displayChatroom && screenWidth < 1000) {
      setDisplayForumNone(true);
    } else {
      setDisplayForumNone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayChatroom]);

  return (
    <div className="forum-chatroom-container">
      <div
        className="forum-page"
        style={{ display: displayForumNone ? "none" : "block" }}
      >
        {forumAddress === null ? (
          <div className="choose-forum-container">
            <img src={Logo} alt="Logo" />
            <h1>Choose a subject on the left &nbsp;👈</h1>
          </div>
        ) : (
          <>
            <h1>{forumAddress}</h1>
            {createPostSelected ? (
              <div className="create-post">
                <form>
                  <button
                    onClick={() => setCreatePostSelected(false)}
                    className="close-btn"
                  >
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                  <div className="input-container">
                    <input
                      className="input"
                      placeholder="Title"
                      onChange={(e) => setNewPostTitle(e.target.value)}
                      value={newPostTitle}
                      maxLength={100}
                    />
                    <textarea
                      className="input"
                      placeholder="Type your message here..."
                      onChange={(e) => setNewPostText(e.target.value)}
                      value={newPostText}
                      maxLength={2000}
                    />
                  </div>
                  <div className="end-container">
                    {selectedImagePreview ? (
                      <div className="selected-image-container">
                        <img
                          className="selected-image-preview"
                          src={selectedImagePreview}
                          alt="Selected Preview"
                        />
                        <button
                          className="deselect-image-button"
                          onClick={handleDeselectImage}
                        >
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    ) : (
                      <label className="upload-image">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          style={{ display: "none" }}
                          className="choose-img-btn"
                        />
                        <div className="choose-image">
                          <p>Click here to upload an image</p>
                        </div>
                      </label>
                    )}

                    <button
                      type="submit"
                      className="send-button"
                      onClick={handleSubmit}
                    >
                      Post
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="search-container">
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="🔍 Search for a post here..."
                />
                <button onClick={() => setCreatePostSelected(true)}>
                  New Post
                </button>
              </div>
            )}
            <div className="display-posts-container">
              <div
                className="display-posts"
                ref={displayPostsRef}
                // style={{ height: createPostSelected ? "28rem" : "41.7rem" }}
                // style={{ height: createPostSelected ? "54vh" : "100vh" }}
              >
                {!firstPostsHaveLoaded ? (
                  <h1>Loading Posts...</h1>
                ) : (
                  <>
                    {loadPosts.map((post) => (
                      <div
                        className="post"
                        key={post.id}
                        onClick={() => {
                          enterChatroom(post);
                        }}
                      >
                        <header>
                          <h3>{post.username}</h3>
                          <h4>
                            {post.createdAt && formatTimestamp(post.createdAt)}
                          </h4>
                        </header>

                        <div className="post-content">
                          <h2>{post.title}</h2>
                          {post.image && (
                            <img
                              src={post.image}
                              alt="User Uploaded"
                              className="user-image"
                            />
                          )}

                          <p>{post.text}</p>
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="loading">Loading more posts...</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {displayChatroom && (
        <ChatRoom
          forumAddress={forumAddress}
          chatroomId={chatroomId}
          setDisplayChatroom={setDisplayChatroom}
          setShowMenu={setShowMenu}
          username={username}
          chatroomTitle={chatroomTitle}
          selectedPost={selectedPost}
          setSelectedPost={setSelectedPost}
          displayForumNone={displayForumNone}
        />
      )}
    </div>
  );
};

export default ForumPage;
